// Lazy mux.js wrapper that turns MPEG-TS segments into fragmented-MP4 bytes.
//
// mux.js is event-based and buffers until `flush()`. We drive it one TS segment
// at a time (push → flush) so output can be streamed to disk incrementally
// rather than held in memory. The init segment (ftyp+moov) is emitted exactly
// once, ahead of the first media fragment; every subsequent segment yields only
// its moof+mdat. The result, concatenated in order, is a single playable .mp4.
//
// `createTransmuxer()` dynamic-imports mux.js (kept out of the main bundle, like
// p2p-media-loader). `makeTransmuxer(mux)` is the pure, unit-testable core.

interface MuxSegment {
  initSegment?: ArrayBuffer | Uint8Array;
  data: ArrayBuffer | Uint8Array;
}
interface MuxTransmuxer {
  on(event: 'data', cb: (segment: MuxSegment) => void): void;
  push(bytes: Uint8Array): void;
  flush(): void;
}
export interface MuxLike {
  mp4: { Transmuxer: new (opts?: Record<string, unknown>) => MuxTransmuxer };
}

export interface Transmuxer {
  /** Feed one TS segment; returns the fMP4 bytes it produced (init once, then media). */
  push(tsBytes: Uint8Array): Uint8Array[];
  /** Finalize; returns any trailing fMP4 bytes. */
  flush(): Uint8Array[];
}

function toU8(buf: ArrayBuffer | Uint8Array): Uint8Array {
  return buf instanceof Uint8Array ? buf : new Uint8Array(buf);
}

export function makeTransmuxer(mux: MuxLike): Transmuxer {
  const transmuxer = new mux.mp4.Transmuxer({ remux: true, keepOriginalTimestamps: true });
  let collected: Uint8Array[] = [];
  let initEmitted = false;

  transmuxer.on('data', (segment) => {
    if (!initEmitted && segment.initSegment) {
      collected.push(toU8(segment.initSegment));
      initEmitted = true;
    }
    collected.push(toU8(segment.data));
  });

  const drain = (): Uint8Array[] => {
    const out = collected;
    collected = [];
    return out;
  };

  return {
    push(tsBytes: Uint8Array): Uint8Array[] {
      transmuxer.push(tsBytes);
      transmuxer.flush();
      return drain();
    },
    flush(): Uint8Array[] {
      transmuxer.flush();
      return drain();
    },
  };
}

export async function createTransmuxer(): Promise<Transmuxer> {
  const mod = await import('mux.js');
  const mux = ((mod as unknown as { default?: MuxLike }).default ?? (mod as unknown as MuxLike));
  return makeTransmuxer(mux);
}
